import { Button, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { outcomeLines } from '../../domain/adoptToMaster.js';
import { useAdoptToMaster, type AdoptToMasterTarget } from '../../hooks/useAdoptToMaster.js';
import { errorMessage } from '../../utils/errors.js';

export type { AdoptToMasterTarget };

/**
 * **Adopt to master**'s confirm. **Adopt as override** only ever touches one environment; this changes master's
 * default for every environment, so the dialog opens on a server dry run
 * (`adoptToBasePreview`, a query: reviewing writes no audit row) and lists what happens where
 * before anything moves. Confirming writes master only; the push that follows is its own step.
 */
export const AdoptToMasterDialog = ({
    target,
    onClose,
}: {
    target: AdoptToMasterTarget | null;
    onClose: () => void;
}) =>
    target ? (
        <AdoptToMasterReview
            key={`${target.request.environment}/${target.request.key}/${target.request.field}`}
            target={target}
            onClose={onClose}
        />
    ) : null;

const AdoptToMasterReview = ({ target, onClose }: { target: AdoptToMasterTarget; onClose: () => void }) => {
    const { preview, write, request } = useAdoptToMaster(target, onClose);
    const env = <span className="rgs-numeric">{request.environment}</span>;

    return (
        <Modal opened onClose={onClose} title="Adopt to master" size="min(520px, 100%)" centered>
            {preview.isLoading ? (
                <Group justify="center" p="lg">
                    <Loader size="sm" />
                </Group>
            ) : preview.isError ? (
                <Stack gap="md">
                    <Text size="sm">{errorMessage(preview.error)}</Text>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={onClose}>
                            Close
                        </Button>
                    </Group>
                </Stack>
            ) : (
                <Stack gap="md">
                    <Text size="sm">
                        {env}'s <b>{target.fieldLabel}</b> of <b>{target.recordLabel}</b> becomes master's default, for
                        every environment that does not override it.
                    </Text>
                    <Stack gap={6}>
                        {outcomeLines(preview.data?.environments ?? []).map((line) => (
                            <Text key={line.heading} size="sm">
                                {line.heading}:{' '}
                                {line.environments.map((name, i) => (
                                    <span key={name}>
                                        {i > 0 && ', '}
                                        <span className="rgs-numeric">{name}</span>
                                    </span>
                                ))}
                            </Text>
                        ))}
                    </Stack>
                    <Text size="xs" c="dimmed">
                        No environment is written.
                    </Text>
                    {write.isError && (
                        <Text size="sm" c="red">
                            {errorMessage(write.error)}
                        </Text>
                    )}
                    <Group justify="flex-end">
                        <Button variant="default" onClick={onClose} disabled={write.isPending}>
                            Cancel
                        </Button>
                        <Button
                            loading={write.isPending}
                            onClick={() => write.mutate({ ...request, expectedRevision: preview.data!.revision })}
                        >
                            Adopt to master
                        </Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    );
};
